import {
  ArrowRed,
  Box,
  ChevronSmall,
  Disclaimer,
  displayCentsInDollars,
  Div,
  FinancesTopIncomeLocationEntity,
  formatAmount,
  getFinancesTopIncomeLocations,
  getTimeZone,
  H3,
  H5,
  Icon,
  lightTheme,
  ListSpinner,
  ModalPortal,
  NumberType,
  P,
  reportError,
  TooltipIcon,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  SERVICE_FEE_LABEL,
  SERVICE_FEE_TOOLTIP,
  VDRIVE_NEGATIVE_FEE_LABEL,
  VDRIVE_NEGATIVE_FEE_TOOLTIP,
} from './serviceLegend';

const SUMMARY_SIZE = 5;
const PAGE_SIZE = 10;

// Service breakdown labels — same ordering as the pie legend. Every service
// has to be listed: the rows are what `total` is made of, and a missing one
// makes the modal look like it does not add up.
const LOCATION_SERVICE_LABELS: {
  key: keyof FinancesTopIncomeLocationEntity;
  label: string;
  tooltip?: string;
}[] = [
  { key: 'vGrubs', label: 'vGrubs VR' },
  { key: 'vOrders', label: 'vOrders' },
  { key: 'saas', label: 'Monthly Saas' },
  { key: 'vDrive', label: 'vDrive' },
  {
    key: 'vDriveNegativeFee',
    label: VDRIVE_NEGATIVE_FEE_LABEL,
    tooltip: VDRIVE_NEGATIVE_FEE_TOOLTIP,
  },
  { key: 'serviceFee', label: SERVICE_FEE_LABEL, tooltip: SERVICE_FEE_TOOLTIP },
  { key: 'withdraw', label: 'Withdrawal Fee' },
];

const IncomeLocationRow = ({
  item,
  index,
  onClick,
}: {
  item: FinancesTopIncomeLocationEntity;
  index: number;
  onClick: () => void;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    window.open(
      `/locations/${item.locationId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };
  return (
    <Div
      $flex
      $alignCenter
      $between
      $borderBottom
      $borderColor={lightTheme.lightGray}
      $height="70px"
      $heightSM="70px"
      $gap="12px"
      $pointer
      onClick={onClick}
    >
      <Div>
        <P
          $bold
          $mb="4"
          $left
          $lineHeight="1.2"
          $pointer
          $hoverUnderline
          onClick={handleClick}
        >
          {index + 1}. {item.locationName}
        </P>
        <Disclaimer $m="0" $left>
          Total Income:{' '}
          <span style={{ color: lightTheme.green }}>
            {displayCentsInDollars(item.total)}
          </span>
        </Disclaimer>
      </Div>
      <Icon $rotate="270deg" color={lightTheme.lightGray}>
        <ChevronSmall />
      </Icon>
    </Div>
  );
};

/**
 * Per-location services breakdown drilldown. Renders the service rows from
 * the location entity directly — no extra fetch is needed since the API now
 * embeds the breakdown on each top-income-locations row.
 */
const IncomeLocationDetailsModal = ({
  location,
  onClose,
}: {
  location: FinancesTopIncomeLocationEntity | null;
  onClose: () => void;
}) => (
  <ModalPortal
    isOpen={location != null}
    closeModal={onClose}
    backgroundColor={lightTheme.white}
    padding="24px"
    logo={false}
    width="560px"
    modalOverflow="auto"
    titleWithExit={location?.locationName ?? ''}
    zIndex={135}
  >
    {location && (
      <>
        <H5 $mb="8">By Service</H5>
        {LOCATION_SERVICE_LABELS.map(({ key, label, tooltip }) => (
          <Div
            key={key}
            $flex
            $alignCenter
            $between
            $borderBottom
            $borderColor={lightTheme.lightGray}
            $p="10px 0"
          >
            <Div $flex $alignCenter $gap="8px">
              <P $m="0">{label}</P>
              {tooltip && <TooltipIcon tooltip={tooltip} />}
            </Div>
            <P $bold $m="0" $color={lightTheme.green}>
              {/* `?? 0` so a service the API has not started sending yet
                  renders as $0.00 instead of $NaN. */}
              {formatAmount((location[key] as number) ?? 0, NumberType.USD)}
            </P>
          </Div>
        ))}

        <H3 $mb="0" $mt="16" $right $color={lightTheme.green}>
          {formatAmount(location.total, NumberType.USD)}
        </H3>
      </>
    )}
  </ModalPortal>
);

/**
 * Top revenue locations card. The summary shows the first 5 (driven by a
 * single page-1 fetch); the "See all" arrow opens a modal that paginates the
 * full list with an in-view sentinel.
 */
export const IncomeLocationsList = ({
  from,
  to,
}: {
  from: string;
  to: string;
}) => {
  const [summaryItems, setSummaryItems] = useState<
    FinancesTopIncomeLocationEntity[]
  >([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] =
    useState<FinancesTopIncomeLocationEntity | null>(null);
  const [modalItems, setModalItems] = useState<
    FinancesTopIncomeLocationEntity[]
  >([]);
  const [modalPage, setModalPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [sentinelRef, sentinelInView] = useInView();

  // Summary (top 5) — re-fetched whenever the date range changes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSummaryLoading(true);
      try {
        const res = await getFinancesTopIncomeLocations({
          page: 1,
          limit: SUMMARY_SIZE,
          from: DateTime.fromISO(from).startOf('day').toISODate() || '',
          to: DateTime.fromISO(to).endOf('day').toISODate() || '',
          timeZone: getTimeZone,
        });
        if (!cancelled) setSummaryItems(res.data.data ?? []);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.data.message !== 'Unauthorized'
        ) {
          reportError(error);
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  // Reset modal list when the underlying date range changes.
  useEffect(() => {
    setModalItems([]);
    setModalPage(1);
    setHasMore(true);
  }, [from, to]);

  const fetchModalPage = async (nextPage: number) => {
    if (modalLoading) return;
    setModalLoading(true);
    try {
      const res = await getFinancesTopIncomeLocations({
        page: nextPage,
        limit: PAGE_SIZE,
        from: DateTime.fromISO(from).startOf('day').toISODate() || '',
        to: DateTime.fromISO(to).endOf('day').toISODate() || '',
        timeZone: getTimeZone,
      });
      const next = res.data.data ?? [];
      setModalItems((prev) => (nextPage === 1 ? next : [...prev, ...next]));
      setHasMore(next.length === PAGE_SIZE);
      setModalPage(nextPage);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setModalLoading(false);
    }
  };

  // First modal fetch on open.
  useEffect(() => {
    if (isOpen && modalItems.length === 0 && hasMore && !modalLoading) {
      fetchModalPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Continue paginating while sentinel is in view.
  useEffect(() => {
    if (
      sentinelInView &&
      isOpen &&
      hasMore &&
      !modalLoading &&
      modalItems.length > 0
    ) {
      fetchModalPage(modalPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  return (
    <>
      <Box
        $p="16px 20px"
        $relative
        $overflow="hidden"
        $height="100%"
        $flex
        $column
      >
        {summaryItems.length > 0 && (
          <Icon
            $pointer
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setIsOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        )}
        <H5 $mb="16">Top Revenue Locations</H5>

        {/* Fixed body height (5 rows) so cards stay uniform regardless of
            how many rows / whether empty. */}
        <Div $minHeight="350px">
          {summaryItems.map((item, index) => (
            <IncomeLocationRow
              key={item.locationId}
              item={item}
              index={index}
              onClick={() => setSelected(item)}
            />
          ))}

          {summaryItems.length === 0 && !summaryLoading && (
            <Div $flex $center $height="350px">
              <P $m="0">No data available.</P>
            </Div>
          )}
        </Div>
      </Box>

      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit="Top Revenue Locations"
      >
        {modalItems.map((item, index) => (
          <IncomeLocationRow
            key={item.locationId}
            item={item}
            index={index}
            onClick={() => setSelected(item)}
          />
        ))}
        <ListSpinner loading={modalLoading} observerRef={sentinelRef} />
      </ModalPortal>

      <IncomeLocationDetailsModal
        location={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};
